import { useState, useEffect, useRef } from 'react';
import axios from '@common/axios';

const useQuizNavigation = (conceptId) => {
    const [quizData, setQuizData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [showQuestions, setShowQuestions] = useState(false);

    // Replaces questionList:
    const [resultsMap, setResultsMap] = useState({});
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [loadingQuestions, setLoadingQuestions] = useState(false);

    const isStartingQuiz = useRef(false);

    // Initial load logic remains unchanged
    useEffect(() => {
        if (!conceptId) return;
        const loadQuizData = async () => {
            try {
                setLoading(true);
                const response = await axios.get('/web/ui/question/list', { params: { conceptId } });
                const normalisedLevels = (response.data.levelsData || []).map(l => ({
                    ...l, questionCount: l.questionCount ?? 0,
                }));

                setQuizData({ ...response.data, levelsData: normalisedLevels });
                if (normalisedLevels.length > 0) {
                    setSelectedLevel(normalisedLevels.find(l => l.questionCount > 0) || normalisedLevels[0]);
                }
            } catch (err) {
                setError('Failed to load quizzes');
            } finally {
                setLoading(false);
            }
        };
        loadQuizData();
    }, [conceptId]);

    const fetchResultsMap = async (levelValue) => {
        try {
            const res = await axios.get('/web/ui/question/results', {
                params: { conceptId, level: levelValue }
            });
            setResultsMap(res.data || {});

            // Return the data so we can use it immediately in handleStartQuiz
            return res.data || {};
        } catch (err) {
            console.warn('Failed to fetch results map:', err);
            return {};
        }
    };
    // Load a specific question, or let the backend auto-fetch the next unanswered one
    const loadQuestion = async (targetId = null, levelValue = selectedLevel?.value) => {
        if (!levelValue) return;
        setLoadingQuestions(true);
        try {
            const url = `/web/ui/question/load${targetId ? `/${targetId}` : ''}`;
            const response = await axios.get(url, {
                params: { conceptId, level: levelValue }
            });
            if (response.data && response.data.questionId) {
                setCurrentQuestion(response.data);
            }
        } catch (err) {
            console.error(`Error loading question ${targetId || 'auto'}:`, err);
        } finally {
            setLoadingQuestions(false);
        }
    };


    const handleStartQuiz = async () => {
        if (!selectedLevel || isStartingQuiz.current) return;
        isStartingQuiz.current = true;
        try {
            setShowQuestions(true);
            setLoadingQuestions(true);

            // 1. Fetch the results map
            const results = await fetchResultsMap(selectedLevel.value);
            const resultsArray = Object.values(results);

            // 2. Calculate the frontier question ID
            let targetQuestionId = null;
            const frontierIndex = resultsArray.findIndex(q => q.result === undefined || q.result === null);

            if (frontierIndex !== -1) {
                // Set to the first unanswered question
                targetQuestionId = resultsArray[frontierIndex].questionId;
            } else if (resultsArray.length > 0) {
                // If all questions are answered, default to the last question
                targetQuestionId = resultsArray[resultsArray.length - 1].questionId;
            }

            // 3. Explicitly load the frontier question instead of passing 'null'
            await loadQuestion(targetQuestionId, selectedLevel.value);

        } catch (err) {
            setError('Failed to start quiz');
        } finally {
            setLoadingQuestions(false);
            isStartingQuiz.current = false;
        }
    };

    const reloadResults = async () => {
        if (selectedLevel) await fetchResultsMap(selectedLevel.value);
    };

    const resetNavigation = () => {
        setShowQuestions(false);
        setCurrentQuestion(null);
        setResultsMap({});
    };

    return {
        quizData, loading, error, selectedLevel, setSelectedLevel,
        showQuestions, resultsMap, currentQuestion, loadingQuestions,
        handleStartQuiz, loadQuestion, reloadResults, resetNavigation
    };
};

export default useQuizNavigation;